<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { tStore } from '../../i18n'
  import { tenderFiltersApi, type TenderFilter } from '../../api/tenderFilters'
  import PageHeader from '../../components/PageHeader.svelte'
  import CpvPicker from '../../components/CpvPicker.svelte'
  import PlatformPicker from '../../components/PlatformPicker.svelte'
  import SavedFiltersView from './SavedFiltersView.svelte'
  import FilterDeleteConfirmModal from './filters/FilterDeleteConfirmModal.svelte'
  import { useToast } from '../../composables/useToast'
  import { useAbortable } from '../../composables/useAbortable'
  import { filterRunErrorMessage } from '../../composables/filterRunError'
  import { isApiError } from '../../api/errors'
  import { activeFilterId, initActiveFilterId, setActiveFilterId } from '../../stores/tenderFilter'
  import { setUnsavedChanges, requestLeave } from '../../stores/unsavedChanges'

  const toast = useToast()
  const { start } = useAbortable()

  let activeTab = $state<'cpv' | 'saved'>('cpv')

  let filters = $state<TenderFilter[]>([])
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)

  let showForm = $state(false)
  let editingId = $state<number | undefined>(undefined)
  let saving = $state(false)
  let form = $state({
    name: '',
    sourceIds: [] as number[],
    cpvCodes: [] as string[],
    autoCollect: true,
  })

  // Snapshot the form takes on open (the saved filter for Edit/Edit Company
  // Default, or the empty defaults for New Filter) — isFormDirty compares
  // the live form against this rather than against `filters`, so a
  // concurrent list refresh can't change what counts as "saved".
  type FormSnapshot = typeof form
  let savedBaseline = $state<FormSnapshot | null>(null)

  let runningId = $state<number | undefined>(undefined)
  let deletingId = $state<number | undefined>(undefined)
  let deleteTarget = $state<TenderFilter | null>(null)

  // The company default (OGCS Default) is the one filter with isDefault —
  // it never appears in the managed-filters table below; it gets its own
  // button + Edit Company Default link in the page header instead.
  let companyDefault = $derived(filters.find((f) => f.isDefault))
  let customFilters = $derived(filters.filter((f) => !f.isDefault))

  let isEditingCompanyDefault = $derived(
    showForm && editingId !== undefined && editingId === companyDefault?.id,
  )

  // True as soon as the open form (New, Edit, or Edit Company Default)
  // differs from its saved baseline — order-independent for the pickers,
  // since they build their selection by click order while the backend
  // returns sourceIds/cpvCodes sorted.
  let isFormDirty = $derived.by(() => {
    if (!showForm || !savedBaseline) return false
    return (
      form.name.trim() !== savedBaseline.name.trim() ||
      form.autoCollect !== savedBaseline.autoCollect ||
      !sameSet(form.cpvCodes, savedBaseline.cpvCodes) ||
      !sameSet(form.sourceIds, savedBaseline.sourceIds)
    )
  })

  let isCompanyDefaultDirty = $derived(isEditingCompanyDefault && isFormDirty)

  function sameSet<T>(a: T[], b: T[]): boolean {
    if (a.length !== b.length) return false
    const bSet = new Set(b)
    return a.every((item) => bSet.has(item))
  }

  $effect(() => {
    setUnsavedChanges(isFormDirty)
  })

  onMount(() => {
    void initActiveFilterId()
    void load()
  })

  onDestroy(() => {
    setUnsavedChanges(false)
  })

  async function load() {
    const signal = start()
    loading = true
    errorMsg = null
    try {
      filters = await tenderFiltersApi.list({ signal })
    } catch (err) {
      if (signal.aborted) return
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  function openCreate() {
    editingId = undefined
    form = { name: '', sourceIds: [], cpvCodes: [], autoCollect: true }
    savedBaseline = { name: '', sourceIds: [], cpvCodes: [], autoCollect: true }
    showForm = true
  }

  function openEdit(f: TenderFilter) {
    editingId = f.id
    form = {
      name: f.name,
      sourceIds: [...f.sourceIds],
      cpvCodes: [...f.cpvCodes],
      autoCollect: f.autoCollect,
    }
    savedBaseline = {
      name: f.name,
      sourceIds: [...f.sourceIds],
      cpvCodes: [...f.cpvCodes],
      autoCollect: f.autoCollect,
    }
    showForm = true
  }

  function closeForm() {
    showForm = false
    editingId = undefined
    savedBaseline = null
  }

  function errorText(err: unknown): string {
    return isApiError(err) ? err.message : err instanceof Error ? err.message : String(err)
  }

  async function saveForm() {
    if (!form.name.trim() || form.sourceIds.length === 0 || form.cpvCodes.length === 0 || saving)
      return
    saving = true
    try {
      const input = {
        name: form.name.trim(),
        sourceIds: form.sourceIds,
        cpvCodes: form.cpvCodes,
        autoCollect: form.autoCollect,
      }
      if (isEditingCompanyDefault) {
        const updated = await tenderFiltersApi.updateCompanyDefault(input)
        filters = filters.map((f) => (f.id === updated.id ? updated : f))
      } else if (editingId !== undefined) {
        const updated = await tenderFiltersApi.update(editingId, input)
        filters = filters.map((f) => (f.id === updated.id ? updated : f))
      } else {
        const created = await tenderFiltersApi.create(input)
        filters = [...filters, created]
      }
      toast.show($tStore('views.tenderFilters.saved'), 'success')
      closeForm()
    } catch (err) {
      toast.show(errorText(err), 'error')
    } finally {
      saving = false
    }
  }

  function submit(e: Event) {
    e.preventDefault()
    void saveForm()
  }

  function requestDelete(f: TenderFilter) {
    deleteTarget = f
  }

  function cancelDelete() {
    if (deletingId !== undefined) return
    deleteTarget = null
  }

  async function executeDeletion() {
    if (!deleteTarget || deletingId !== undefined) return
    const f = deleteTarget
    deletingId = f.id
    try {
      await tenderFiltersApi.remove(f.id)
      filters = filters.filter((x) => x.id !== f.id)
      if (f.id === $activeFilterId) setActiveFilterId(companyDefault?.id)
      toast.show($tStore('views.tenderFilters.deleted'), 'success')
      deleteTarget = null
    } catch (err) {
      toast.show(errorText(err), 'error')
    } finally {
      deletingId = undefined
    }
  }

  function handleCompanyDefaultClick() {
    if (!companyDefault) return
    if (isCompanyDefaultDirty) {
      void saveForm()
    } else {
      setActiveFilterId(companyDefault.id)
    }
  }

  async function run(f: TenderFilter) {
    runningId = f.id
    try {
      const result = await tenderFiltersApi.run(f.id)
      toast.show($tStore('views.tenderFilters.runStarted', { name: f.name }), 'success')
      const summary = result.results
        .map((r) => `${r.sourceName}: ${$tStore(`views.tenderFilters.runStatus.${r.status}`)}`)
        .join(', ')
      if (summary) toast.show(summary, 'info')
    } catch (err) {
      toast.show(filterRunErrorMessage(err), 'error')
    } finally {
      runningId = undefined
    }
  }
</script>

<section class="filters-page">
  <PageHeader
    title={$tStore('views.tenderFilters.title')}
    subtitle={$tStore('views.tenderFilters.subtitle')}
  >
    {#snippet actions()}
      {#if activeTab === 'cpv'}
        <div class="header-actions">
          {#if companyDefault}
            <div class="company-default">
              <button
                type="button"
                class="btn btn--primary"
                disabled={!isCompanyDefaultDirty && $activeFilterId === companyDefault.id}
                onclick={handleCompanyDefaultClick}
              >
                {isCompanyDefaultDirty
                  ? $tStore('views.tenderFilters.saveCompanyDefault')
                  : companyDefault.name}
              </button>
              <button
                type="button"
                class="company-default__edit"
                disabled={isCompanyDefaultDirty}
                onclick={() => companyDefault && requestLeave(() => openEdit(companyDefault))}
              >
                {$tStore('views.tenderFilters.editCompanyDefault')}
              </button>
            </div>
          {/if}
          <button
            class="btn btn--primary"
            onclick={() => (showForm ? requestLeave(closeForm) : openCreate())}
          >
            {showForm ? $tStore('common.cancel') : $tStore('views.tenderFilters.new')}
          </button>
        </div>
      {/if}
    {/snippet}
  </PageHeader>

  <div class="tabs" role="tablist">
    <button
      type="button"
      role="tab"
      class="tab"
      class:tab--active={activeTab === 'cpv'}
      aria-selected={activeTab === 'cpv'}
      onclick={() => (activeTab = 'cpv')}
    >
      {$tStore('views.tenderFilters.tabCpv')}
    </button>
    <button
      type="button"
      role="tab"
      class="tab"
      class:tab--active={activeTab === 'saved'}
      aria-selected={activeTab === 'saved'}
      onclick={() =>
        requestLeave(() => {
          closeForm()
          activeTab = 'saved'
        })}
    >
      {$tStore('views.tenderFilters.tabSaved')}
    </button>
  </div>

  {#if activeTab === 'cpv'}
    <div class="cpv-tab">
      {#if showForm}
        <form class="card form" onsubmit={submit}>
          <label class="field">
            <span class="field__label">{$tStore('views.tenderFilters.form.name')}</span>
            <input class="input" type="text" bind:value={form.name} required />
          </label>

          <div class="field">
            <span class="field__label">{$tStore('views.tenderFilters.form.platforms')}</span>
            <PlatformPicker bind:selected={form.sourceIds} />
          </div>

          <div class="field">
            <span class="field__label">{$tStore('views.tenderFilters.form.cpvCodes')}</span>
            <CpvPicker bind:selected={form.cpvCodes} />
          </div>

          <label class="checkbox-row">
            <input type="checkbox" bind:checked={form.autoCollect} />
            {$tStore('views.tenderFilters.form.autoCollect')}
          </label>

          <div class="form__actions">
            <button
              type="button"
              class="btn btn--secondary"
              onclick={() => requestLeave(closeForm)}
            >
              {$tStore('common.cancel')}
            </button>
            <button
              type="submit"
              class="btn btn--primary"
              disabled={saving ||
                !form.name.trim() ||
                form.sourceIds.length === 0 ||
                form.cpvCodes.length === 0}
            >
              {saving ? $tStore('common.saving') : $tStore('common.save')}
            </button>
          </div>
        </form>
      {/if}

      {#if loading}
        <p class="muted">{$tStore('views.tenderFilters.loading')}</p>
      {:else if errorMsg}
        <p class="error">{errorMsg}</p>
      {:else if customFilters.length === 0}
        <p class="muted">{$tStore('views.tenderFilters.empty')}</p>
      {:else}
        <div class="card table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>{$tStore('views.tenderFilters.columns.name')}</th>
                <th>{$tStore('views.tenderFilters.columns.platformCount')}</th>
                <th>{$tStore('views.tenderFilters.columns.cpvCount')}</th>
                <th>{$tStore('views.tenderFilters.columns.autoCollect')}</th>
                <th class="actions">{$tStore('views.tenderFilters.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {#each customFilters as f (f.id)}
                <tr>
                  <td>{f.name}</td>
                  <td class="mono">{f.sourceIds.length}</td>
                  <td class="mono">{f.cpvCodes.length}</td>
                  <td>{f.autoCollect ? $tStore('common.yes') : $tStore('common.no')}</td>
                  <td class="actions">
                    <button
                      class="btn btn--sm btn--secondary"
                      disabled={runningId === f.id}
                      onclick={() => run(f)}
                    >
                      {$tStore('views.tenderFilters.actions.run')}
                    </button>
                    <button
                      class="btn btn--sm btn--secondary"
                      onclick={() => requestLeave(() => openEdit(f))}
                    >
                      {$tStore('views.tenderFilters.actions.edit')}
                    </button>
                    <button
                      class="btn btn--sm btn--danger"
                      disabled={deletingId === f.id}
                      onclick={() => requestDelete(f)}
                    >
                      {$tStore('views.tenderFilters.actions.delete')}
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  {:else}
    <SavedFiltersView />
  {/if}
</section>

<FilterDeleteConfirmModal
  target={deleteTarget}
  isActive={deleteTarget?.id === $activeFilterId}
  defaultFilterName={companyDefault?.name}
  inFlight={deletingId !== undefined}
  onConfirm={executeDeletion}
  onCancel={cancelDelete}
/>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .filters-page {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .tabs {
    display: flex;
    gap: v.$space-xs;
    border-bottom: 1px solid v.$color-border;
  }

  .tab {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: v.$space-sm v.$space-md;
    font-size: 0.875rem;
    font-weight: 500;
    color: v.$color-text-muted;
    cursor: pointer;

    &:hover {
      color: v.$color-text;
    }

    &--active {
      color: v.$color-primary;
      border-bottom-color: v.$color-primary;
    }
  }

  .cpv-tab {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .header-actions {
    display: flex;
    align-items: flex-start;
    gap: v.$space-md;
  }

  .company-default {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: v.$space-xs;
  }

  .company-default__edit {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.75rem;
    color: v.$color-primary;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;

    &:disabled {
      color: v.$color-text-muted;
      text-decoration: none;
      cursor: not-allowed;
    }
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: v.$space-md;
  }

  .checkbox-row {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    font-size: 0.8125rem;
  }

  .form__actions {
    display: flex;
    justify-content: flex-end;
    gap: v.$space-sm;
  }

  .table-wrap {
    overflow-x: auto;
    padding: 0;
  }

  .actions {
    width: 1%;
    white-space: nowrap;
    text-align: right;

    :global(.btn) {
      margin-left: v.$space-xs;
    }
  }
</style>
